/**
 * DonorProfiles.jsx  — Phase 1
 * List, create and manage donor organisation profiles.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  getDonors, createDonor, updateDonor, deleteDonor,
  DONOR_TYPES, DONOR_STATUSES, fmtDate,
} from '../../../api/donorsApi';

// ── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ value }) {
  const s = DONOR_STATUSES.find(x => x.value === value) || { label: value, color: '#6B7280' };
  return (
    <span className="bb-dn-badge" style={{ background: s.color + '22', color: s.color, border: `1px solid ${s.color}44` }}>
      {s.label}
    </span>
  );
}

// ── Donor Form Modal ──────────────────────────────────────────────────────────
function DonorFormModal({ donor, orgId, onClose, onSaved }) {
  const isEdit = !!donor;
  const [form, setForm] = useState({
    name:       donor?.name       || '',
    type:       donor?.type       || 'corporate',
    status:     donor?.status     || 'prospect',
    website:    donor?.website    || '',
    notes:      donor?.notes      || '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Donor name is required.'); return; }
    setSaving(true); setError('');
    try {
      if (isEdit) await updateDonor(donor.id, form);
      else         await createDonor(orgId, form);
      onSaved();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bb-dn-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bb-dn-modal">
        <div className="bb-dn-modal-header">
          <h3>{isEdit ? 'Edit Donor' : 'Add New Donor'}</h3>
          <button className="bb-dn-modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="bb-dn-alert bb-dn-alert--error">{error}</div>}

        <div className="bb-dn-form-grid">
          <div className="bb-dn-form-group bb-dn-full">
            <label>Organisation Name <span className="bb-dn-required">*</span></label>
            <input className="bb-dn-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Acme Foundation" />
          </div>

          <div className="bb-dn-form-group">
            <label>Donor Type</label>
            <select className="bb-dn-select" value={form.type} onChange={e => set('type', e.target.value)}>
              {DONOR_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div className="bb-dn-form-group">
            <label>Status</label>
            <select className="bb-dn-select" value={form.status} onChange={e => set('status', e.target.value)}>
              {DONOR_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          <div className="bb-dn-form-group bb-dn-full">
            <label>Website</label>
            <input className="bb-dn-input" value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://..." />
          </div>

          <div className="bb-dn-form-group bb-dn-full">
            <label>Notes</label>
            <textarea className="bb-dn-textarea" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Internal notes about this donor..." />
          </div>
        </div>

        <div className="bb-dn-modal-footer">
          <button className="bb-dn-btn bb-dn-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="bb-dn-btn bb-dn-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Donor'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Delete confirm modal ───────────────────────────────────────────────────────
function DeleteConfirmModal({ donor, onClose, onConfirm, deleting }) {
  return (
    <div className="bb-dn-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bb-dn-modal" style={{ maxWidth: 440 }}>
        <div className="bb-dn-modal-header">
          <h3>Delete Donor</h3>
          <button className="bb-dn-modal-close" onClick={onClose}>✕</button>
        </div>
        <p style={{ padding: '16px 0', color: 'var(--bb-gray)' }}>
          Are you sure you want to delete <strong>{donor.name}</strong>? This action cannot be undone.
        </p>
        <div className="bb-dn-modal-footer">
          <button className="bb-dn-btn bb-dn-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="bb-dn-btn bb-dn-btn-danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete Donor'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function DonorProfiles({ orgId, onSelectDonor }) {
  const [donors,   setDonors]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editDonor, setEditDonor] = useState(null);
  const [deleteDonorItem, setDeleteDonorItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setDonors(await getDonors(orgId)); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const filtered = donors.filter(d => {
    const matchSearch = !search || d.name?.toLowerCase().includes(search.toLowerCase());
    const matchType   = typeFilter   === 'all' || d.type   === typeFilter;
    const matchStatus = statusFilter === 'all' || d.status === statusFilter;
    return matchSearch && matchType && matchStatus;
  });

  const handleDelete = async () => {
    if (!deleteDonorItem) return;
    setDeleting(true);
    try {
      await deleteDonor(deleteDonorItem.id);
      setDeleteDonorItem(null);
      load();
    } catch (e) { console.error(e); }
    finally { setDeleting(false); }
  };

  if (loading) return <div className="bb-dn-loading"><div className="bb-loading-spinner" /><p>Loading donors…</p></div>;

  return (
    <div className="bb-dn-section">
      {/* Header */}
      <div className="bb-dn-section-header">
        <div>
          <h2 className="bb-dn-section-title">Donor Profiles</h2>
          <p className="bb-dn-section-subtitle">Manage your organisation's donor relationships</p>
        </div>
        <button className="bb-dn-btn bb-dn-btn-primary" onClick={() => { setEditDonor(null); setShowForm(true); }}>
          + Add Donor
        </button>
      </div>

      {/* Filters */}
      <div className="bb-dn-filters">
        <input
          className="bb-dn-input bb-dn-search"
          placeholder="Search donors…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="bb-dn-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="all">All Types</option>
          {DONOR_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select className="bb-dn-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          {DONOR_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <span className="bb-dn-count">{filtered.length} donor{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="bb-dn-empty">
          <div className="bb-dn-empty-icon">🏢</div>
          <p>{donors.length === 0 ? 'No donors added yet. Click "Add Donor" to get started.' : 'No donors match your filters.'}</p>
        </div>
      ) : (
        <div className="bb-dn-table-wrap">
          <table className="bb-dn-table">
            <thead>
              <tr>
                <th>Organisation</th>
                <th>Type</th>
                <th>Status</th>
                <th>Website</th>
                <th>Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => {
                const typeLabel = DONOR_TYPES.find(t => t.value === d.type)?.label || d.type;
                return (
                  <tr key={d.id} className="bb-dn-table-row">
                    <td>
                      <button
                        className="bb-dn-donor-name-link"
                        onClick={() => onSelectDonor && onSelectDonor(d)}
                      >
                        {d.name}
                      </button>
                    </td>
                    <td><span className="bb-dn-type-pill">{typeLabel}</span></td>
                    <td><StatusBadge value={d.status} /></td>
                    <td>
                      {d.website
                        ? <a href={d.website} target="_blank" rel="noreferrer" className="bb-dn-link">{d.website.replace(/^https?:\/\//, '')}</a>
                        : <span className="bb-dn-muted">—</span>}
                    </td>
                    <td className="bb-dn-muted">{fmtDate(d.createdAt)}</td>
                    <td>
                      <div className="bb-dn-row-actions">
                        <button className="bb-dn-action-btn" onClick={() => { setEditDonor(d); setShowForm(true); }}>Edit</button>
                        <button className="bb-dn-action-btn bb-dn-action-btn--danger" onClick={() => setDeleteDonorItem(d)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <DonorFormModal
          donor={editDonor}
          orgId={orgId}
          onClose={() => { setShowForm(false); setEditDonor(null); }}
          onSaved={() => { setShowForm(false); setEditDonor(null); load(); }}
        />
      )}

      {deleteDonorItem && (
        <DeleteConfirmModal
          donor={deleteDonorItem}
          onClose={() => setDeleteDonorItem(null)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}
    </div>
  );
}
